import { SpartenType } from '@ps20/bff-models';
import { App } from './ps20apps';

/**
 * Maps verkaufsangebotKurzname to the appropriate SpartenType.
 * This is especially important for TKV and VWG where different product codes map to different versions.
 *
 * TM001-TM008: Legacy TKV products -> TIERKRANKEN
 * TM009-TM032: New TKV_V2 products -> TIERKRANKEN_V2
 * EH001-EH004: Legacy VWG products -> WOHNGEBAEUDE
 * EH005-EH008: New VWG_V2 products -> WOHNGEBAEUDE_V2
 */
export function getSpartenTypeByVerkaufsangebotKurzname(
  verkaufsangebotKurzname: string,
): SpartenType | null {
  if (!verkaufsangebotKurzname) {
    return null;
  }

  const tkvLegacyProducts = [
    'TM001',
    'TM002',
    'TM003',
    'TM004',
    'TM005',
    'TM006',
    'TM007',
    'TM008',
  ];
  const tkvV2Products = [
    'TM009',
    'TM010',
    'TM011',
    'TM012',
    'TM013',
    'TM014',
    'TM015',
    'TM016',
    'TM017',
    'TM018',
    'TM019',
    'TM020',
    'TM021',
    'TM022',
    'TM023',
    'TM024',
    'TM025',
    'TM026',
    'TM027',
    'TM028',
    'TM029',
    'TM030',
    'TM031',
    'TM032',
  ];
  const vwgLegacyProducts = ['EH001', 'EH002', 'EH003', 'EH004'];
  const vwgV2Products = ['EH005', 'EH006', 'EH007', 'EH008'];

  if (tkvLegacyProducts.includes(verkaufsangebotKurzname)) {
    return SpartenType.TIERKRANKEN;
  }

  if (tkvV2Products.includes(verkaufsangebotKurzname)) {
    return SpartenType.TIERKRANKEN_V2;
  }

  if (vwgLegacyProducts.includes(verkaufsangebotKurzname)) {
    return SpartenType.WOHNGEBAEUDE;
  }

  if (vwgV2Products.includes(verkaufsangebotKurzname)) {
    return SpartenType.WOHNGEBAEUDE_V2;
  }

  return null;
}

/**
 * Gets the appropriate App type based on verkaufsangebotKurzname.
 * This is a convenience wrapper around getSpartenTypeByVerkaufsangebotKurzname.
 */
export function getAppByVerkaufsangebotKurzname(verkaufsangebotKurzname: string): App | null {
  const spartenType = getSpartenTypeByVerkaufsangebotKurzname(verkaufsangebotKurzname);

  if (spartenType === SpartenType.TIERKRANKEN) {
    return App.TKV;
  }

  if (spartenType === SpartenType.TIERKRANKEN_V2) {
    return App.TKV_V2;
  }

  if (spartenType === SpartenType.WOHNGEBAEUDE) {
    return App.VWG;
  }

  if (spartenType === SpartenType.WOHNGEBAEUDE_V2) {
    return App.VWG_V2;
  }

  return null;
}

/**
 * Determines the correct SpartenType for vorvertrag data by checking verkaufsangebotKurznamen array.
 * This helps identify whether a contract is TKV or TKV_V2 based on its product codes.
 *
 * @param verkaufsangebotKurznamen - Array of product codes from the contract
 * @returns SpartenType if determinable from product codes, null otherwise
 */
export function getSpartenTypeFromVerkaufsangebotKurznamen(
  verkaufsangebotKurznamen: string[] | undefined,
): SpartenType | null {
  if (!verkaufsangebotKurznamen || verkaufsangebotKurznamen.length === 0) {
    return null;
  }

  for (const kurzname of verkaufsangebotKurznamen) {
    const spartenType = getSpartenTypeByVerkaufsangebotKurzname(kurzname);
    if (spartenType) {
      return spartenType;
    }
  }

  return null;
}

/**
 * Determines if a verkaufsangebotKurzname belongs to TKV legacy products
 */
export function isTkvLegacyProduct(verkaufsangebotKurzname: string): boolean {
  return (
    getSpartenTypeByVerkaufsangebotKurzname(verkaufsangebotKurzname) === SpartenType.TIERKRANKEN
  );
}

/**
 * Determines if a verkaufsangebotKurzname belongs to TKV_V2 products
 */
export function isTkvV2Product(verkaufsangebotKurzname: string): boolean {
  return (
    getSpartenTypeByVerkaufsangebotKurzname(verkaufsangebotKurzname) === SpartenType.TIERKRANKEN_V2
  );
}
